import {
  Shield,
  Zap,
  TrendingUp,
  CheckCircle2,
  DollarSign,
  Award,
  FileText,
  Users,
} from "lucide-react";

export default function SevenRsSection() {
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-zinc-50" data-testid="7rs-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 text-sky-700 text-sm font-semibold mb-6">
            <Shield className="w-4 h-4" />
            Our Core Philosophy
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            The ResiliencyHUB Philosophy: <span className="text-sky-700">The Power of 7Rs</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
            Our mission is simple: deliver enterprise-grade business resiliency through a friendly, intuitive tool with the <strong>lowest total cost of ownership</strong> and the <strong>fastest, highest return on investment</strong> in the industry.
          </p>
        </div>

        {/* Battle-Tested Framework */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8 md:p-12 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-sky-700 to-sky-800 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">007</span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900">Our Battle-Tested Framework</h3>
              <p className="text-slate-500 text-sm">The Seven Rs of Resilience</p>
            </div>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            ResiliencyHUB is built on our proprietary{" "}
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/1cd8adc1-abab-4f5f-9fef-e69c912c561f/images/f5f480fa047088f3ea388678a1ad9d0e229f20174d5c3486a8ce3121248ffee6.png" 
              alt="007 Philosophy" 
              className="inline-block h-20 align-middle mx-2"
            />
            —the Seven Rs of Resilience. Our business function-centric framework aligns seamlessly with your organization's vision and mission, delivering comprehensive protection through:
          </p>
          
          {/* Core 3Rs - Response, Recovery, Restoration */}
          <div className="bg-gradient-to-r from-sky-700 to-sky-800 rounded-2xl p-6 md:p-8 mb-8 text-white">
            <p className="text-center text-xl md:text-2xl font-bold mb-6 tracking-wide text-white">
              Rapid Response. Efficient Recovery. Reliable Restoration.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
                <div className="w-14 h-14 bg-red-500/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2">Rapid <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-500 text-white font-black text-base">R</span>esponse</h4>
                <p className="text-sky-100 text-sm">Act decisively when disruption strikes</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
                <div className="w-14 h-14 bg-sky-700/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2">Efficient <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-700 text-white font-black text-base">R</span>ecovery</h4>
                <p className="text-sky-100 text-sm">Restore critical business processes with speed and precision</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
                <div className="w-14 h-14 bg-yellow-500/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2">Reliable <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-yellow-400 text-slate-900 font-black text-base">R</span>estoration</h4>
                <p className="text-sky-100 text-sm">Return to full business operations with confidence</p>
              </div>
            </div>
          </div>

          {/* Protecting What Matters Most - 4Rs */}
          <div className="mb-8">
            <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-600" />
              Protecting What Matters Most:
            </h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="group p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <h5 className="font-bold text-slate-900 text-lg mb-2"><span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white font-black text-base mr-1">R</span>evenue</h5>
                <p className="text-slate-600 text-sm">Safeguard your income streams and financial stability</p>
              </div>
              <div className="group p-5 rounded-xl border-2 border-slate-200 hover:border-sky-400 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Award className="w-6 h-6 text-sky-700" />
                </div>
                <h5 className="font-bold text-slate-900 text-lg mb-2"><span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-700 text-white font-black text-base mr-1">R</span>eputation</h5>
                <p className="text-slate-600 text-sm">Preserve customer trust and brand integrity</p>
              </div>
              <div className="group p-5 rounded-xl border-2 border-slate-200 hover:border-sky-400 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-sky-700" />
                </div>
                <h5 className="font-bold text-slate-900 text-lg mb-2"><span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-700 text-white font-black text-base mr-1">R</span>egulatory Compliance</h5>
                <p className="text-slate-600 text-sm">Meet industry requirements and avoid penalties</p>
              </div>
              <div className="group p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <h5 className="font-bold text-slate-900 text-lg mb-2"><span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white font-black text-base mr-1">R</span>esources</h5>
                <p className="text-slate-600 text-sm">Protect your people, facilities, and intellectual property</p>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center py-6 border-t border-slate-200">
            <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-700 via-sky-700 to-slate-700 bg-clip-text text-transparent">
              Simple to use. Powerful in protection. Proven in practice.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
